import React from "react";
import { Box, Drawer, IconButton, Typography, alpha } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { NAVY, GOLD, TEXT_SECONDARY } from "../../theme/tokens";

export interface SlidePanelProps {
  open: boolean;
  onClose: () => void;
  title: string;
  subtitle?: React.ReactNode;
  icon?: React.ReactElement;
  /** Sticky footer (e.g. Save / Cancel). */
  footer?: React.ReactNode;
  width?: number;
  children: React.ReactNode;
}

/**
 * Right-side slide-over for create/edit forms and detail views. Premium header
 * (icon tile + title + subtitle), scrollable body, optional sticky footer.
 */
export default function SlidePanel({
  open,
  onClose,
  title,
  subtitle,
  icon,
  footer,
  width = 480,
  children,
}: SlidePanelProps) {
  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{ sx: { width: { xs: "100%", sm: width }, maxWidth: "100%" } }}
    >
      <Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
        {/* Header */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1.5,
            px: 2.5,
            py: 2,
            borderBottom: "1px solid",
            borderColor: "divider",
          }}
        >
          {icon && (
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: 2.5,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                background: `linear-gradient(135deg, ${NAVY} 0%, #071521 100%)`,
                border: `1px solid ${alpha(GOLD, 0.35)}`,
                flexShrink: 0,
              }}
            >
              {React.cloneElement(icon, { sx: { fontSize: 20, color: GOLD } })}
            </Box>
          )}
          <Box sx={{ flexGrow: 1, minWidth: 0 }}>
            <Typography fontSize={17} fontWeight={800} color={NAVY} noWrap>
              {title}
            </Typography>
            {subtitle && (
              <Typography fontSize={12} color={TEXT_SECONDARY} noWrap>
                {subtitle}
              </Typography>
            )}
          </Box>
          <IconButton onClick={onClose} size="small">
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>

        {/* Body */}
        <Box sx={{ flexGrow: 1, overflowY: "auto", p: 2.5 }}>{children}</Box>

        {/* Footer */}
        {footer && (
          <Box
            sx={{
              px: 2.5,
              py: 2,
              borderTop: "1px solid",
              borderColor: "divider",
              display: "flex",
              gap: 1,
              justifyContent: "flex-end",
              bgcolor: "background.paper",
            }}
          >
            {footer}
          </Box>
        )}
      </Box>
    </Drawer>
  );
}
